import { SiteLayout } from "@/components/SiteLayout";
import { Container } from "@/components/Container";
import type { CmsLegalPage } from "@/lib/cms/website.functions";

/** Shared renderer for every CMS-managed legal/policy page. */
export function LegalPageView({
  page,
  fallbackTitle,
}: {
  page: CmsLegalPage | null;
  fallbackTitle: string;
}) {
  return (
    <SiteLayout>
      <Container className="py-16">
        <article className="mx-auto max-w-3xl">
          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            {page?.title || fallbackTitle}
          </h1>
          {page?.last_updated_at ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Last updated{" "}
              {new Date(page.last_updated_at).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          ) : null}
          {page ? (
            <div
              className="prose prose-sm mt-8 max-w-none dark:prose-invert"
              dangerouslySetInnerHTML={{ __html: page.body_html }}
            />
          ) : (
            <p className="mt-8 text-sm text-muted-foreground">
              This policy has not been published yet. Please check back shortly or contact our
              support team.
            </p>
          )}
        </article>
      </Container>
    </SiteLayout>
  );
}

/** Head metadata built from the CMS record, with sensible fallbacks. */
export function legalHead(
  page: CmsLegalPage | null,
  fallbackTitle: string,
  path: string,
) {
  const title = page?.seo_title || `${page?.title || fallbackTitle} — Poonam Kapur Healthy Kitchen`;
  const description =
    page?.meta_description ||
    `${page?.title || fallbackTitle} for Poonam Kapur Healthy Kitchen meal delivery in Mumbai.`;
  const url = page?.canonical_url?.startsWith("https://poonamkapur.com/") ? page.canonical_url : `https://poonamkapur.com${path}`;
  return {
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: url }],
  };
}
